import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowRight } from "lucide-react";
import { MIN_SEARCH_LENGTH, useProductSearch } from "@/hooks/use-product-search";
import { ProductResultRow } from "@/components/ProductResultRow";
import { Skeleton } from "@/components/ui/skeleton";
import { formatPrice } from "@/lib/utils";

const MAX_SUGGESTIONS = 8;

interface SearchDropdownProps {
  searchQuery: string;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  /** Wrapper around the search input and this list — clicks inside it keep the list open. */
  containerRef: React.RefObject<HTMLElement>;
}

export function SearchDropdown({
  searchQuery,
  isOpen,
  setIsOpen,
  containerRef,
}: SearchDropdownProps) {
  // The previous results stay up while the next ones load, so the list doesn't blink on
  // every keystroke. "Nothing found" must wait for an answer to *this* query, though:
  // until then the list is either stale or empty, and saying "ничего не найдено" is a lie.
  const { isTooShort, items, isSearching } = useProductSearch(searchQuery, {
    enabled: isOpen,
    pageSize: MAX_SUGGESTIONS,
  });

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isOpen, setIsOpen]);

  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, setIsOpen, containerRef]);

  if (!isOpen || !searchQuery) return null;

  return (
    <div className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-lg border border-border/90 bg-card shadow-lifted">
      <div className="max-h-[70dvh] overflow-y-auto overscroll-contain" aria-live="polite">
        {isTooShort ? (
          <div className="p-6 text-center text-sm text-muted-foreground">
            Введите минимум {MIN_SEARCH_LENGTH} символа
          </div>
        ) : items.length > 0 ? (
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="card-title">Товары</h3>
              <Link
                to={`/search?q=${encodeURIComponent(searchQuery)}`}
                onClick={() => setIsOpen(false)}
                className="text-xs text-primary hover:text-primary/80 flex items-center gap-1"
              >
                Все результаты
                <ArrowRight className="size-4" />
              </Link>
            </div>
            <div className="space-y-2">
              {items.map((product) => (
                <ProductResultRow
                  key={product.id}
                  product={product}
                  to={`/product/${product.id}`}
                  onClick={() => setIsOpen(false)}
                  highlight={searchQuery}
                  trailing={
                    <span className="shrink-0 text-sm font-semibold text-foreground">
                      {formatPrice(product.priceWholesale)} ₽
                    </span>
                  }
                />
              ))}
            </div>
          </div>
        ) : isSearching ? (
          <div className="p-4 space-y-2" aria-busy="true">
            <p className="sr-only">Ищем…</p>
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 p-2">
                <Skeleton className="size-12 rounded-lg" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/3" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-6 text-center">
            <Search className="mx-auto mb-3 size-12 text-muted-foreground" />
            <div className="text-sm text-muted-foreground">
              По запросу «{searchQuery}» ничего не найдено
            </div>
            <div className="text-xs text-muted-foreground mt-1">Попробуйте изменить запрос</div>
          </div>
        )}
      </div>
    </div>
  );
}
